import React, { Component } from 'react';
import { PropTypes } from 'prop-types';
import { connect } from 'react-redux';
import {
  startOfYear,
  endOfYear,
  startOfMonth,
  endOfMonth,
  endOfDay,
  subYears,
  subMonths,
  addDays,
  isBefore,
  isAfter,
  isValid,
  differenceInYears,
  format,
} from 'date-fns';
import { withStyles } from '@mui/styles';
import {
  MenuItem,
  Button,
  Popover,
  FormControl,
  Select,
  InputLabel,
  TextField,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import DateRangeIcon from '@mui/icons-material/DateRange';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { setDateRange } from 'store/actions/appActions';
import { DateUtil, StoreUtil } from 'doctivity-shared/utils';

// app.dateRange.start/end are stored as ISO strings (they survive redux-persist
// as strings anyway). Normalize to a Date wherever a Date is needed.
const toDate = (v) => (v instanceof Date ? v : new Date(v));

const styles = (theme) => ({
  dateRangeButton: {
    color: theme.palette.secondary.main,
    '&:hover': {
      backgroundColor: 'transparent',
      color: theme.palette.secondary.bright,
    },
    fontWeight: 400,
  },
  rangeContainer: {
    padding: 20,
    display: 'flex',
    flexDirection: 'column',
  },
  rangeControl: {
    minWidth: 120,
    marginBottom: 20,
  },
  dateRangeIcon: {
    marginRight: theme.spacing(),
  },
});

class DateRangeSelector extends Component {
  constructor(props) {
    super(props);
    this.state = {
      rangeMenuOpen: false,
      dateRangeError: undefined,
      debounceTimer: null,
    };
  }

  render() {
    const { classes, app } = this.props;
    const { rangeMenuOpen } = this.state;

    const startDate = this.getStartDate();

    const lastMonth = endOfMonth(startDate);
    const lastYear = subYears(addDays(lastMonth, 1), 1);
    const last365 = 'Last 365 Days';

    let dateRange = app.dateRange;
    if (!dateRange) {
      // set to default, nothing set yet
      dateRange = {
        start: lastYear.toISOString(),
        end: lastMonth.toISOString(),
        name: last365,
        preset: 3,
      };
    }

    return (
      <>
        <Button
          className={classes.dateRangeButton}
          onClick={() => {
            this.setState({ rangeMenuOpen: !rangeMenuOpen });
          }}
          endIcon={<ExpandMoreIcon />}
          ref={(b) => {
            this.rangeButtonRef = b;
          }}
        >
          <DateRangeIcon className={classes.dateRangeIcon} />
          <div>{dateRange.name}</div>
        </Button>
        <Popover
          open={rangeMenuOpen}
          anchorEl={this.rangeButtonRef}
          onClose={() => {
            this.closeRangeMenu();
          }}
        >
          <div className={classes.rangeContainer}>
            <FormControl className={classes.rangeControl}>
              <InputLabel>Date Range</InputLabel>
              <Select
                label='Date Range'
                value={dateRange.preset || 0}
                onChange={this.changePresetDate}
              >
                <MenuItem value={1}>Last Year</MenuItem>
                <MenuItem value={2}>Year to Date</MenuItem>
                <MenuItem value={3}>{last365}</MenuItem>
                <MenuItem value={4}>
                  {format(subMonths(startDate, 1), 'MMMM yyyy')}
                </MenuItem>
                <MenuItem value={5}>{format(startDate, 'MMMM yyyy')}</MenuItem>
                <MenuItem value={0}>Custom Range</MenuItem>
              </Select>
            </FormControl>
            <FormControl className={classes.rangeControl}>
              <DatePicker
                renderInput={(params) => <TextField {...params} />}
                label='From'
                name='from'
                value={toDate(dateRange.start)}
                onChange={this.changeStartDate}
                format={DateUtil.LDML_DISPLAY_FORMAT}
                maxDate={startDate}
                disableFuture={false}
                disablePast={false}
                disabled={dateRange.preset > 0}
              />
            </FormControl>
            <FormControl className={classes.rangeControl}>
              <DatePicker
                renderInput={(params) => <TextField {...params} />}
                label='To'
                value={toDate(dateRange.end)}
                onChange={this.changeEndDate}
                name='to'
                format={DateUtil.LDML_DISPLAY_FORMAT}
                minDate={toDate(dateRange.start)}
                maxDate={endOfDay(endOfMonth(startDate))}
                disableFuture={false}
                disablePast={false}
                disabled={dateRange.preset > 0}
              />
            </FormControl>
            {this.state.dateRangeError && (
              <div style={{ fontFamily: 'Roboto', color: 'red' }}>{this.state.dateRangeError}</div>
            )}
          </div>
        </Popover>
      </>
    );
  }

  getStartDate() {
    const { currentClient, clients } = this.props;

    let client = null;
    if (currentClient) {
      client = currentClient;
    } else if (!client && clients && Array.isArray(clients)) {
      client = clients[0];
    }

    let startDate = null;
    if (client && client.most_recent_date) {
      startDate = new Date(client.most_recent_date);
    }

    if (!startDate) {
      startDate = subMonths(new Date(), 1);
    }
    return startDate;
  }

  closeRangeMenu() {
    this.setState({
      rangeMenuOpen: false,
      dateRangeError: undefined,
    });
  }

  changePresetDate = (event) => {
    const { dispatch, app } = this.props;

    const startDate = this.getStartDate();

    let newRange;
    const value = event.target.value;
    if (value === 0) {
      // custom range — keep the current start/end (already ISO strings), just
      // switch to the custom preset so the pickers become editable.
      newRange = { ...app.dateRange, preset: 0 };
    } else if (value === 1) {
      // last year
      newRange = {
        name: 'Last Year',
        start: startOfYear(subYears(startDate, 1)).toISOString(),
        end: endOfYear(subYears(startDate, 1)).toISOString(),
        preset: value,
      };
    } else if (value === 2) {
      // year to date
      const { useClientDate, currentClient } = this.props;
      const selectedEnd = endOfDay(startDate);
      const maxDate =
        currentClient &&
        isBefore(new Date(currentClient.most_recent_date), selectedEnd)
          ? new Date(currentClient.most_recent_date)
          : selectedEnd;
      const end = useClientDate ? maxDate : selectedEnd;
      newRange = {
        name: 'Year to Date',
        start: startOfYear(startDate).toISOString(),
        end: end.toISOString(),
        preset: value,
      };
    } else if (value === 3) {
      // last 365 days
      const lastMonth = endOfMonth(startDate);
      const lastYear = subYears(addDays(lastMonth, 1), 1);
      newRange = {
        name: 'Last 365 Days',
        start: lastYear.toISOString(),
        end: lastMonth.toISOString(),
        preset: value,
      };
    } else if (value === 4) {
      // 2 months ago
      newRange = {
        name: format(subMonths(startDate, 1), 'MMMM yyyy'),
        start: startOfMonth(subMonths(startDate, 1)).toISOString(),
        end: endOfMonth(subMonths(startDate, 1)).toISOString(),
        preset: value,
      };
    } else if (value === 5) {
      // month to date
      newRange = {
        name: format(startDate, 'MMMM yyyy'),
        start: startOfMonth(startDate).toISOString(),
        end: endOfMonth(startDate).toISOString(),
        preset: value,
      };
    }

    if (newRange) {
      dispatch(setDateRange(newRange));
      if (newRange.preset > 0) {
        this.closeRangeMenu();
      }
    }
  };

  validateDateRange = (from, to) => {
    const start = toDate(from);
    const end = toDate(to);
    if (!isValid(start)) {
      this.setState({
        dateRangeError: 'Invalid start date',
      });
    } else if (!isValid(end)) {
      this.setState({
        dateRangeError: 'Invalid end date',
      });
    } else if (isAfter(start, end)) {
      this.setState({
        dateRangeError: 'Start date must be before end date',
      });
    } else if (differenceInYears(end, start) > 3) {
      this.setState({
        dateRangeError: 'Date range cannot exceed 3 years',
      });
    } else {
      this.setState({
        dateRangeError: undefined,
      });
      return true;
    }
    return false;
  };

  changeStartDate = (value) => {
    const { app } = this.props;
    if (this.validateDateRange(value, app.dateRange.end)) {
      this.changeDateRange(value, app.dateRange.end);
    }
  };

  changeEndDate = (value) => {
    const { app } = this.props;
    if (this.validateDateRange(app.dateRange.start, value)) {
      this.changeDateRange(app.dateRange.start, value);
    }
  };

  changeDateRange = (start, end) => {
    const { dispatch } = this.props;
    const { debounceTimer } = this.state;
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    this.setState({
      debounceTimer: setTimeout(() => {
        dispatch(
          setDateRange({
            start: toDate(start).toISOString(),
            end: toDate(end).toISOString(),
            name: `${DateUtil.formatDateForUser(start)} - ${DateUtil.formatDateForUser(end)}`,
          }),
        );
      }, 500),
    });
  };
}

DateRangeSelector.propTypes = {
  dispatch: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
  app: PropTypes.object,
  clients: PropTypes.array,
  selectedClient: PropTypes.number,
  useClientDate: PropTypes.bool,
  currentClient: PropTypes.object,
};

function mapStateToProps(state) {
  const {
    app: { selectedClient },
    clients,
  } = state;
  const list = StoreUtil.get(clients, StoreUtil.COMMON_LIST);
  const currentClient = list?.data?.find((c) => c.id === selectedClient);

  return {
    app: state.app,
    currentClient,
  };
}

const styled = withStyles(styles)(DateRangeSelector);
const connected = connect(mapStateToProps)(styled);
export { connected as DateRangeSelector };
